<template>
  <div class="college-safety-report">
    <!-- Header Section -->
    <v-row class="mb-6">
      <v-col cols="12">
        <div class="header-section">
          <div class="d-flex align-center justify-space-between flex-wrap">
            <div>
              <h1 class="page-title">
                <v-icon large color="white" class="mr-3">mdi-shield-account</v-icon>
                รายงานข้อมูลลูกจ้าง ประกันสังคม
              </h1>
              <p class="page-subtitle mt-2">
                ระบบติดตามและตรวจสอบการรายงานประกันสังคมของสถานศึกษา สังกัด สอศ.
              </p>
            </div>
            <div class="d-flex align-center header-badges">
              <v-chip class="mr-3 header-chip" dark>
                <v-icon left small>mdi-calendar</v-icon>
                ปีงบประมาณ {{ getCurrentYear() }}
              </v-chip>
              <v-chip class="header-chip" color="white" outlined>
                <v-icon left small color="white">mdi-domain</v-icon>
                <span class="white--text">{{ budget_etcs.length }} สถานศึกษา</span>
              </v-chip>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Statistics Cards -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-primary" elevation="4">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <p class="stat-label mb-1">สถานศึกษาทั้งหมด</p>
                <h2 class="stat-value">{{ formatNumber(statistics.total) }}</h2>
                <p class="stat-percent">แห่ง</p>
              </div>
              <v-avatar size="56" color="primary" class="stat-icon">
                <v-icon dark size="28">mdi-domain</v-icon>
              </v-avatar>
            </div>
            <v-progress-linear 
              :value="100" 
              color="primary" 
              height="4" 
              rounded
              class="mt-3"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-success" elevation="4">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <p class="stat-label mb-1">รายงานครบถ้วน</p>
                <h2 class="stat-value">{{ formatNumber(countsCheck.succ) }}</h2>
                <p class="stat-percent">{{ statistics.successPercent }}%</p>
              </div>
              <v-avatar size="56" color="success" class="stat-icon">
                <v-icon dark size="28">mdi-check-circle</v-icon>
              </v-avatar>
            </div>
            <v-progress-linear 
              :value="statistics.successPercent" 
              color="success" 
              height="4" 
              rounded
              class="mt-3"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-error" elevation="4">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <p class="stat-label mb-1">รายงานไม่ครบ</p>
                <h2 class="stat-value">{{ formatNumber(countsCheck.fals) }}</h2>
                <p class="stat-percent">{{ statistics.failPercent }}%</p>
              </div>
              <v-avatar size="56" color="error" class="stat-icon">
                <v-icon dark size="28">mdi-alert-circle</v-icon>
              </v-avatar>
            </div>
            <v-progress-linear 
              :value="statistics.failPercent" 
              color="error" 
              height="4" 
              rounded
              class="mt-3"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-teal" elevation="4">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <p class="stat-label mb-1">รวมลูกจ้างประกันสังคม</p>
                <h2 class="stat-value">{{ formatNumber(statistics.totalSafety) }}</h2>
                <p class="stat-percent">คน</p>
              </div>
              <v-avatar size="56" color="teal" class="stat-icon">
                <v-icon dark size="28">mdi-shield-check</v-icon>
              </v-avatar>
            </div>
            <v-progress-linear 
              :value="100" 
              color="teal" 
              height="4" 
              rounded
              class="mt-3"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts Section -->
    <v-row class="mb-6">
      <v-col cols="12" md="4">
        <v-card class="chart-card" elevation="4">
          <v-card-title class="chart-title">
            <v-icon color="teal" class="mr-2">mdi-chart-donut</v-icon>
            สถานะการรายงานประกันสังคม
          </v-card-title>
          <v-card-text>
            <apexchart
              ref="donutChart"
              type="donut"
              height="300"
              :options="donutChartOptions"
              :series="donutSeries"
            ></apexchart>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="8">
        <v-card class="chart-card" elevation="4">
          <v-card-title class="chart-title">
            <v-icon color="teal" class="mr-2">mdi-chart-bar</v-icon>
            เปรียบเทียบจำนวนลูกจ้างและประกันสังคม (Top 15)
          </v-card-title>
          <v-card-text>
            <apexchart
              ref="barChart"
              type="bar"
              height="300"
              :options="barChartOptions"
              :series="barSeries"
            ></apexchart>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filter Section -->
    <v-row class="mb-4">
      <v-col cols="12" md="4">
        <v-select
          v-model="filterStatus"
          :items="filterOptions"
          label="กรองตามสถานะ"
          prepend-inner-icon="mdi-filter-variant"
          outlined
          dense
          clearable
        ></v-select>
      </v-col>
    </v-row>

    <!-- Data Table -->
    <v-row>
      <v-col cols="12">
        <v-card class="data-table-card" elevation="4">
          <v-card-title class="table-title d-flex justify-space-between flex-wrap">
            <div class="d-flex align-center">
              <v-icon color="teal" class="mr-2">mdi-table</v-icon>
              <span>รายละเอียดการรายงานประกันสังคมของสถานศึกษา</span>
            </div>
            <div class="d-flex align-center table-actions">
              <v-btn
                color="success"
                class="mr-2"
                @click="exportToExcel()"
                :disabled="!filteredData.length"
              >
                <v-icon left>mdi-microsoft-excel</v-icon>
                ส่งออก Excel
              </v-btn>
              <v-btn
                color="teal"
                outlined
                @click="refreshData()"
                :loading="loading"
              >
                <v-icon left>mdi-refresh</v-icon>
                รีเฟรช
              </v-btn>
            </div>
          </v-card-title>

          <v-card-text>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="ค้นหาสถานศึกษา..."
              outlined
              dense
              clearable
              hide-details
              class="mb-4 search-field"
            ></v-text-field>

            <v-data-table
              :loading="loading"
              :headers="headers"
              :items="filteredData"
              :search="search"
              :items-per-page="15"
              :footer-props="{
                'items-per-page-options': [10, 15, 25, 50, -1],
                'items-per-page-text': 'แสดงรายการ:'
              }"
              class="modern-table"
              loading-text="กำลังโหลดข้อมูล..."
              no-data-text="ไม่พบข้อมูล"
              no-results-text="ไม่พบผลลัพธ์ที่ค้นหา"
            >
              <template v-slot:item.user_code="{ item }">
                <v-chip small color="grey lighten-2" class="font-weight-bold">
                  {{ item.user_code }}
                </v-chip>
              </template>

              <template v-slot:item.user_firstname="{ item }">
                <div class="d-flex align-center">
                  <v-avatar size="36" color="teal" class="mr-3">
                    <v-icon dark small>mdi-school</v-icon>
                  </v-avatar>
                  <span class="college-name">{{ item.user_firstname }}</span>
                </div>
              </template>

              <template v-slot:item.countTem="{ item }">
                <v-chip color="info" dark small class="font-weight-bold">
                  {{ formatNumber(item.countTem) }} คน
                </v-chip>
              </template>

              <template v-slot:item.countSafe="{ item }">
                <v-chip 
                  :color="item.countTem === item.countSafe ? 'teal' : 'orange'" 
                  dark 
                  small 
                  class="font-weight-bold"
                >
                  <v-icon left x-small>mdi-shield-check</v-icon>
                  {{ formatNumber(item.countSafe) }} คน
                </v-chip>
              </template>

              <template v-slot:item.difference="{ item }">
                <span :class="getDifferenceClass(item)">
                  {{ getDifference(item) }}
                </span>
              </template>

              <template v-slot:item.coverage="{ item }">
                <div class="coverage-cell">
                  <v-progress-linear
                    :value="calculateCoverage(item)"
                    :color="getCoverageColor(item)"
                    height="20"
                    rounded
                    striped
                  >
                    <template v-slot:default>
                      <span class="coverage-text">{{ calculateCoverage(item) }}%</span>
                    </template>
                  </v-progress-linear>
                </div>
              </template>

              <template v-slot:item.check="{ item }">
                <div class="d-flex align-center justify-center">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        v-bind="attrs"
                        v-on="on"
                        :color="item.countTem === item.countSafe ? 'success' : 'error'"
                        dark
                        small
                        class="status-chip"
                      >
                        <v-icon left small>
                          {{ item.countTem === item.countSafe ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                        </v-icon>
                        {{ item.countTem === item.countSafe ? 'ครบถ้วน' : 'ไม่ครบ' }}
                      </v-chip>
                    </template>
                    <span>
                      {{ item.countTem === item.countSafe 
                        ? 'ลูกจ้างทุกคนมีประกันสังคม' 
                        : `ยังขาดอีก ${Math.abs(item.countTem - item.countSafe)} คน` 
                      }}
                    </span>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">{{ snackbar.icon }}</v-icon>
        {{ snackbar.text }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          ปิด
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import * as XLSX from "xlsx";

export default {
  name: "CollegeSafetyReport",

  components: {
    apexchart: VueApexCharts
  },

  data() {
    return {
      loading: true,
      ApiKey: "HRvec2021",
      valid: true,
      search: "",
      filterStatus: null,
      budget_etcs: [],
      countsCheck: {
        succ: 0,
        fals: 0
      },

      // Filter Options
      filterOptions: [
        { text: "ทั้งหมด", value: null },
        { text: "ประกันสังคมครบถ้วน", value: "success" },
        { text: "ประกันสังคมไม่ครบ", value: "fail" }
      ],

      // Snackbar
      snackbar: {
        show: false,
        color: "",
        timeout: 5000,
        icon: "",
        text: ""
      },

      // Table Headers
      headers: [
        { text: "รหัส", align: "center", value: "user_code", width: "100" },
        { text: "สถานศึกษา", align: "left", value: "user_firstname", width: "250" },
        { text: "จำนวนลูกจ้าง", align: "center", value: "countTem", width: "130" },
        { text: "มีประกันสังคม", align: "center", value: "countSafe", width: "140" },
        { text: "ส่วนต่าง", align: "center", value: "difference", width: "100" },
        { text: "ความครอบคลุม", align: "center", value: "coverage", width: "180" },
        { text: "สถานะ", align: "center", value: "check", width: "120" }
      ],

      // Donut Chart Options
      donutChartOptions: {
        chart: {
          type: "donut",
          fontFamily: "Sarabun, sans-serif",
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800
          }
        },
        labels: ["ประกันสังคมครบถ้วน", "ประกันสังคมไม่ครบ"],
        colors: ["#009688", "#F44336"],
        legend: {
          position: "bottom",
          fontSize: "14px",
          fontFamily: "Sarabun, sans-serif",
          markers: {
            width: 12,
            height: 12,
            radius: 2
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: "70%",
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: "14px",
                  fontFamily: "Sarabun, sans-serif"
                },
                value: {
                  show: true,
                  fontSize: "24px",
                  fontFamily: "Sarabun, sans-serif",
                  fontWeight: "bold",
                  formatter: function(val) {
                    return val + " แห่ง";
                  }
                },
                total: {
                  show: true,
                  label: "รวมทั้งหมด",
                  fontSize: "12px",
                  fontFamily: "Sarabun, sans-serif",
                  formatter: function(w) {
                    return w.globals.seriesTotals.reduce((a, b) => a + b, 0) + " แห่ง";
                  }
                }
              }
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val.toFixed(1) + "%";
          }
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val + " สถานศึกษา";
            }
          }
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: { width: 280 },
            legend: { position: "bottom" }
          }
        }]
      },

      // Bar Chart Options
      barChartOptions: {
        chart: {
          type: "bar",
          fontFamily: "Sarabun, sans-serif",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false
            }
          },
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 4,
            columnWidth: "70%",
            dataLabels: {
              position: "top"
            }
          }
        },
        xaxis: {
          categories: [],
          labels: {
            style: {
              fontSize: "10px",
              fontFamily: "Sarabun, sans-serif"
            },
            rotate: -45,
            rotateAlways: true,
            maxHeight: 80
          }
        },
        yaxis: {
          title: {
            text: "จำนวน (คน)",
            style: {
              fontSize: "12px",
              fontFamily: "Sarabun, sans-serif"
            }
          },
          labels: {
            formatter: function(val) {
              return parseInt(val).toLocaleString();
            }
          }
        },
        colors: ["#2196F3", "#009688"],
        legend: {
          position: "top",
          fontSize: "12px",
          fontFamily: "Sarabun, sans-serif"
        },
        fill: { opacity: 1 },
        dataLabels: {
          enabled: false
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val.toLocaleString() + " คน";
            }
          }
        }
      }
    };
  },

  computed: {
    statistics() {
      const total = this.budget_etcs.length;
      const totalEmployees = this.budget_etcs.reduce((sum, item) => sum + (parseInt(item.countTem) || 0), 0);
      const totalSafety = this.budget_etcs.reduce((sum, item) => sum + (parseInt(item.countSafe) || 0), 0);
      const successPercent = total > 0 ? ((this.countsCheck.succ / total) * 100).toFixed(1) : 0;
      const failPercent = total > 0 ? ((this.countsCheck.fals / total) * 100).toFixed(1) : 0;

      return {
        total,
        totalEmployees,
        totalSafety,
        successPercent,
        failPercent
      };
    },

    donutSeries() {
      return [this.countsCheck.succ || 0, this.countsCheck.fals || 0];
    },

    barSeries() {
      // Get top 15 colleges by employee count
      const top15 = [...this.budget_etcs]
        .sort((a, b) => (parseInt(b.countTem) || 0) - (parseInt(a.countTem) || 0))
        .slice(0, 15);

      return [
        {
          name: "จำนวนลูกจ้าง",
          data: top15.map(item => parseInt(item.countTem) || 0)
        },
        {
          name: "มีประกันสังคม",
          data: top15.map(item => parseInt(item.countSafe) || 0)
        }
      ];
    },

    filteredData() {
      if (!this.filterStatus) {
        return this.budget_etcs;
      }
      
      return this.budget_etcs.filter(item => {
        const isSuccess = item.countTem === item.countSafe;
        return this.filterStatus === "success" ? isSuccess : !isSuccess;
      });
    }
  },

  watch: {
    budget_etcs: {
      handler() {
        this.updateBarChart();
      },
      deep: true
    }
  },

  async mounted() {
    await this.budget_etcQueryAll();
  },

  methods: {
    getCurrentYear() {
      return new Date().getFullYear() + 543;
    },

    formatNumber(num) {
      if (num === null || num === undefined || num === "") return "0";
      return Number(num).toLocaleString("th-TH");
    },

    calculateCoverage(item) {
      if (!item.countTem || item.countTem === 0) return 0;
      const coverage = (item.countSafe / item.countTem) * 100;
      return Math.min(100, Math.round(coverage));
    },

    getCoverageColor(item) {
      const coverage = this.calculateCoverage(item);
      if (coverage >= 100) return "success";
      if (coverage >= 80) return "teal";
      if (coverage >= 60) return "warning";
      return "error";
    },

    getDifference(item) {
      const diff = (parseInt(item.countSafe) || 0) - (parseInt(item.countTem) || 0);
      if (diff === 0) return "✓";
      return diff > 0 ? `+${diff}` : diff.toString();
    },

    getDifferenceClass(item) {
      const diff = (parseInt(item.countSafe) || 0) - (parseInt(item.countTem) || 0);
      if (diff === 0) return "success--text font-weight-bold";
      if (diff > 0) return "teal--text font-weight-bold";
      return "error--text font-weight-bold";
    },

    showSnackbar(color, icon, text) {
      this.snackbar = { show: true, color, icon, text, timeout: 5000 };
    },

    updateBarChart() {
      const top15 = [...this.budget_etcs]
        .sort((a, b) => (parseInt(b.countTem) || 0) - (parseInt(a.countTem) || 0))
        .slice(0, 15);

      const labels = top15.map(item => 
        item.user_firstname ? 
        (item.user_firstname.length > 15 ? item.user_firstname.substring(0, 15) + "..." : item.user_firstname) 
        : "ไม่ระบุ"
      );

      if (this.$refs.barChart) {
        this.$refs.barChart.updateOptions({
          xaxis: {
            categories: labels
          }
        });
      }
    },

    async budget_etcQueryAll() {
      this.loading = true;
      try {
        let result = await this.$http.post("budget_etc.php", {
          ApiKey: this.ApiKey,
          budget_etcidSummaryCollegeSafety: "ok"
        });
        
        this.budget_etcs = result.data || [];
        
        // Calculate success and fail counts
        let successCount = 0;
        let failCount = 0;
        
        this.budget_etcs.forEach(value => {
          if (value.countTem == value.countSafe) {
            successCount += 1;
          } else {
            failCount += 1;
          }
        });
        
        this.countsCheck.succ = successCount;
        this.countsCheck.fals = failCount;

        this.$nextTick(() => {
          this.updateBarChart();
        });
      } catch (error) {
        this.showSnackbar("error", "mdi-alert-circle", "เกิดข้อผิดพลาดในการโหลดข้อมูล");
        console.error("Error loading data:", error);
      } finally {
        this.loading = false;
      }
    },

    async refreshData() {
      await this.budget_etcQueryAll();
      this.showSnackbar("success", "mdi-check-circle", "รีเฟรชข้อมูลสำเร็จ");
    },

    // Export to Excel
    exportToExcel() {
      const exportData = this.filteredData.map(item => ({
        "รหัสสถานศึกษา": item.user_code,
        "สถานศึกษา": item.user_firstname,
        "จำนวนลูกจ้าง (คน)": parseInt(item.countTem) || 0,
        "มีประกันสังคม (คน)": parseInt(item.countSafe) || 0,
        "ส่วนต่าง": this.getDifference(item),
        "ความครอบคลุม (%)": this.calculateCoverage(item) + "%",
        "สถานะ": item.countTem === item.countSafe ? "ครบถ้วน" : "ไม่ครบ"
      }));

      // Add summary rows
      exportData.push({});
      exportData.push({
        "รหัสสถานศึกษา": "สรุป",
        "สถานศึกษา": "",
        "จำนวนลูกจ้าง (คน)": this.statistics.totalEmployees,
        "มีประกันสังคม (คน)": this.statistics.totalSafety,
        "ส่วนต่าง": "",
        "ความครอบคลุม (%)": "",
        "สถานะ": ""
      });
      exportData.push({
        "รหัสสถานศึกษา": "ประกันสังคมครบถ้วน",
        "สถานศึกษา": this.countsCheck.succ + " แห่ง",
        "จำนวนลูกจ้าง (คน)": "",
        "มีประกันสังคม (คน)": "",
        "ส่วนต่าง": "",
        "ความครอบคลุม (%)": this.statistics.successPercent + "%",
        "สถานะ": ""
      });
      exportData.push({
        "รหัสสถานศึกษา": "ประกันสังคมไม่ครบ",
        "สถานศึกษา": this.countsCheck.fals + " แห่ง",
        "จำนวนลูกจ้าง (คน)": "",
        "มีประกันสังคม (คน)": "",
        "ส่วนต่าง": "",
        "ความครอบคลุม (%)": this.statistics.failPercent + "%",
        "สถานะ": ""
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "รายงานประกันสังคม");

      // Set column widths
      const colWidths = [
        { wch: 15 },
        { wch: 40 },
        { wch: 18 },
        { wch: 18 },
        { wch: 12 },
        { wch: 15 },
        { wch: 12 }
      ];
      ws["!cols"] = colWidths;

      XLSX.writeFile(wb, `รายงานประกันสังคมสถานศึกษา_${this.getCurrentYear()}.xlsx`);
      this.showSnackbar("success", "mdi-check-circle", "ส่งออกไฟล์ Excel สำเร็จ");
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;500;600;700&display=swap');

.college-safety-report {
  font-family: 'Sarabun', sans-serif;
}

/* Header Section */
.header-section {
  background: linear-gradient(135deg, #00796B 0%, #004D40 100%);
  border-radius: 16px;
  padding: 24px 32px;
  color: white;
  box-shadow: 0 10px 40px rgba(0, 121, 107, 0.3);
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-subtitle {
  font-size: 1rem;
  opacity: 0.9;
  margin: 0;
}

.header-badges {
  gap: 8px;
}

.header-chip {
  font-weight: 500;
}

/* Statistics Cards */
.stat-card {
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  border-left: 4px solid;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
}

.stat-card-primary { border-left-color: #00796B; }
.stat-card-success { border-left-color: #4CAF50; }
.stat-card-error { border-left-color: #F44336; }
.stat-card-teal { border-left-color: #009688; }

.stat-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
  margin: 0;
}

.stat-percent {
  font-size: 0.8rem;
  color: #999;
  margin: 0;
}

.stat-icon {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

/* Chart Cards */
.chart-card {
  border-radius: 16px;
  overflow: hidden;
}

.chart-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eee;
}

/* Data Table Card */
.data-table-card {
  border-radius: 16px;
  overflow: hidden;
}

.table-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eee;
  padding: 16px 24px;
}

.table-actions {
  gap: 8px;
}

.search-field {
  max-width: 400px;
}

/* Modern Table Styles */
.modern-table {
  border-radius: 8px;
}

.modern-table >>> th {
  background: #f8f9fa !important;
  font-weight: 600 !important;
  font-size: 0.8rem !important;
  color: #333 !important;
  white-space: nowrap;
}

.modern-table >>> td {
  font-size: 0.875rem;
  padding: 12px 16px !important;
}

.modern-table >>> tr:hover {
  background: #e0f2f1 !important;
}

.college-name {
  font-weight: 500;
  color: #333;
}

.status-chip {
  min-width: 90px;
  justify-content: center;
}

.coverage-cell {
  min-width: 150px;
}

.coverage-text {
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
}

/* Responsive */
@media (max-width: 960px) {
  .header-section {
    padding: 16px 20px;
  }
  
  .page-title {
    font-size: 1.25rem;
  }
  
  .stat-value {
    font-size: 1.5rem;
  }
  
  .table-actions {
    margin-top: 12px;
    width: 100%;
    justify-content: flex-start;
  }
}

/* Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-card,
.chart-card,
.data-table-card {
  animation: fadeIn 0.5s ease forwards;
}

.stat-card:nth-child(1) { animation-delay: 0.1s; }
.stat-card:nth-child(2) { animation-delay: 0.2s; }
.stat-card:nth-child(3) { animation-delay: 0.3s; }
.stat-card:nth-child(4) { animation-delay: 0.4s; }
</style>